/**
 * GA4 merchandising helpers for Deconstruct theme.
 * - select_promotion / view_promotion from [data-ga-merch] elements
 * - top_category_click from homepage category tiles
 * - edd_pincode_check / edd_pincode_result from the ClickPost delivery widget
 * Relies on window.__gaPageSource and window.__shopifyCustomerId from theme.liquid.
 * dataLayer pushes stay for the console. They are also sent straight to
 * G-65ZK299761, because gtag/GTM on this page does not forward them.
 */
(function () {
  "use strict";

  var GA4_MEASUREMENT_ID = "G-65ZK299761";

  window.dataLayer = window.dataLayer || [];

  function cookieValue(name) {
    var match = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
    return match ? decodeURIComponent(match[1]) : "";
  }

  function analyticsAllowed() {
    try {
      if (
        window.Shopify &&
        Shopify.customerPrivacy &&
        typeof Shopify.customerPrivacy.analyticsProcessingAllowed === "function"
      ) {
        return Shopify.customerPrivacy.analyticsProcessingAllowed();
      }
    } catch (error) {}
    return true;
  }

  function clientId() {
    var parts = cookieValue("_ga").split(".");
    if (parts.length >= 4) return parts.slice(-2).join(".");
    return Math.floor(Math.random() * 1e10) + "." + Math.floor(Date.now() / 1000);
  }

  function sessionId() {
    var raw = cookieValue("_ga_" + GA4_MEASUREMENT_ID.replace("G-", ""));
    var modern = raw.match(/s(\d+)/);
    if (modern) return modern[1];
    var legacy = raw.split(".");
    if (legacy.length >= 3 && /^\d+$/.test(legacy[2])) return legacy[2];
    return String(Math.floor(Date.now() / 1000));
  }

  function sendToGa4(eventName, params) {
    if (!analyticsAllowed()) return;
    var query = [
      "v=2",
      "tid=" + encodeURIComponent(GA4_MEASUREMENT_ID),
      "cid=" + encodeURIComponent(clientId()),
      "sid=" + encodeURIComponent(sessionId()),
      "sct=1",
      "seg=1",
      "en=" + encodeURIComponent(eventName),
      "dl=" + encodeURIComponent(location.href),
      "dt=" + encodeURIComponent(document.title || ""),
      "ul=" + encodeURIComponent((navigator.language || "").toLowerCase())
    ];
    Object.keys(params).forEach(function (key) {
      var value = params[key];
      if (value == null || value === "") return;
      var prefix = typeof value === "number" ? "epn." : "ep.";
      query.push(prefix + encodeURIComponent(key) + "=" + encodeURIComponent(value));
    });
    var url = "https://www.google-analytics.com/g/collect?" + query.join("&");
    if (!(navigator.sendBeacon && navigator.sendBeacon(url))) {
      fetch(url, { method: "POST", keepalive: true, mode: "no-cors" });
    }
  }

  function pushEvent(payload) {
    var event = Object.assign({}, payload);
    if (window.__shopifyCustomerId) {
      event.user_id = String(window.__shopifyCustomerId);
      event.shopify_customer_id = String(window.__shopifyCustomerId);
    }
    window.dataLayer.push(event);
    if (!event.event) return;
    var params = {};
    Object.keys(event).forEach(function (key) {
      if (key !== "event") params[key] = event[key];
    });
    sendToGa4(event.event, params);
  }

  function readMerchParams(el) {
    if (!el) return null;
    var promo = el.getAttribute("data-ga-promo") || "";
    var content = el.getAttribute("data-ga-content") || "";
    var creative = el.getAttribute("data-ga-creative") || "";
    var position = el.getAttribute("data-ga-position") || "";
    var placement = el.getAttribute("data-ga-placement") || "";
    if (!promo && !content && !creative && !position && !placement) {
      return null;
    }
    return {
      promo: promo,
      content: content,
      creative: creative,
      position: position,
      placement: placement
    };
  }

  function pushPromotion(eventName, params) {
    if (!params) return;
    pushEvent({
      event: eventName,
      promo: params.promo,
      content: params.content,
      creative: params.creative,
      position: params.position,
      placement: params.placement
    });
  }

  function onMerchClick(event) {
    var target = event.target;
    if (!target || typeof target.closest !== "function") return;
    var merch = target.closest("[data-ga-merch]");
    if (!merch) return;
    pushPromotion("select_promotion", readMerchParams(merch));
  }

  function observeViews() {
    var nodes = document.querySelectorAll("[data-ga-merch]");
    if (!nodes.length) return;

    if (!("IntersectionObserver" in window)) {
      for (var i = 0; i < nodes.length; i++) {
        var params = readMerchParams(nodes[i]);
        if (params && !nodes[i].getAttribute("data-ga-viewed")) {
          nodes[i].setAttribute("data-ga-viewed", "1");
          pushPromotion("view_promotion", params);
        }
      }
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          if (el.getAttribute("data-ga-viewed")) {
            observer.unobserve(el);
            return;
          }
          var params = readMerchParams(el);
          if (params) {
            el.setAttribute("data-ga-viewed", "1");
            pushPromotion("view_promotion", params);
          }
          observer.unobserve(el);
        });
      },
      { threshold: 0.35 }
    );

    for (var j = 0; j < nodes.length; j++) {
      observer.observe(nodes[j]);
    }
  }

  /**
   * top_category_click from the homepage category tiles
   * (sections/list_collection.liquid, [data-ga-top-category]).
   */
  function onTopCategoryClick(event) {
    if (event.defaultPrevented) return;
    var target = event.target;
    if (!target || typeof target.closest !== "function") return;
    var card = target.closest("[data-ga-top-category]");
    if (!card) return;
    var position = parseInt(card.getAttribute("data-ga-position"), 10);
    pushEvent({
      event: "top_category_click",
      category_name: card.getAttribute("data-ga-category-name") || "",
      category_url: card.getAttribute("data-ga-category-url") || "",
      position: isNaN(position) ? null : position,
      placement: "header_top_categories",
      source: window.__gaPageSource || "unknown"
    });
  }

  /**
   * Estimated delivery date (ClickPost app block). The app renders the result
   * into #response-success (#response-edd, #response-cod) or #response-error
   * and toggles .button--loading on the Check button while it fetches.
   */
  var EDD_RESULT_TIMEOUT_MS = 30000;
  var eddCheck = null;

  function eddFieldValue(form, name) {
    var input = form.querySelector('[name="' + name + '"]');
    return input ? String(input.value || "").trim() : "";
  }

  function eddContext(form) {
    return {
      pincode: eddFieldValue(form, "drop_pincode"),
      product_id: eddFieldValue(form, "product_id"),
      variant_id: eddFieldValue(form, "variant_id"),
      item_name: eddFieldValue(form, "product_title")
    };
  }

  function isShown(el) {
    return !!el && el.style.display !== "none" && el.offsetParent !== null && !!el.textContent.trim();
  }

  function readEddResult(container, form) {
    var error = container.querySelector("#response-error");
    if (isShown(error)) {
      var message = error.textContent.trim();
      var unserviceable = eddFieldValue(form, "unservicable_text");
      var isUnserviceable =
        /not\s*servic|unservic/i.test(message) || (unserviceable && message === unserviceable);
      return { status: isUnserviceable ? "unserviceable" : "error", message: message };
    }

    var success = container.querySelector("#response-success");
    var edd = container.querySelector("#response-edd");
    if (!isShown(success) || !edd || !edd.textContent.trim()) return null;

    var cod = container.querySelector("#response-cod");
    var codText = cod && !cod.classList.contains("cp-ns-hidden") ? cod.textContent : "";
    var codAvailable = "unknown";
    if (/not\s+available/i.test(codText)) codAvailable = false;
    else if (/available/i.test(codText)) codAvailable = true;

    return {
      status: "serviceable",
      delivery_text: edd.textContent.replace(/\s+/g, " ").trim(),
      cod_available: codAvailable
    };
  }

  function finishEddCheck() {
    if (!eddCheck) return;
    eddCheck.observer.disconnect();
    clearTimeout(eddCheck.debounce);
    clearTimeout(eddCheck.timeout);
    eddCheck = null;
  }

  function evaluateEddCheck() {
    if (!eddCheck) return;
    var button = eddCheck.container.querySelector(".clickpost-edd__submit-button");
    if (button && button.classList.contains("button--loading")) return;
    var result = readEddResult(eddCheck.container, eddCheck.form);
    if (!result) return;

    var payload = Object.assign(
      { event: "edd_pincode_result", source: window.__gaPageSource || "unknown" },
      eddCheck.context,
      result
    );
    finishEddCheck();
    pushEvent(payload);
  }

  function startEddCheck(form) {
    var container = form.closest(".clickpost-edd__container") || form.parentElement;
    if (!container) return;
    var context = eddContext(form);

    finishEddCheck();
    pushEvent(
      Object.assign({ event: "edd_pincode_check", source: window.__gaPageSource || "unknown" }, context)
    );

    var check = { form: form, container: container, context: context, debounce: null, timeout: null };
    check.observer = new MutationObserver(function () {
      clearTimeout(check.debounce);
      check.debounce = setTimeout(evaluateEddCheck, 300);
    });
    check.observer.observe(container, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ["class", "style"]
    });
    check.timeout = setTimeout(finishEddCheck, EDD_RESULT_TIMEOUT_MS);
    eddCheck = check;
  }

  function onEddClick(event) {
    var target = event.target;
    if (!target || typeof target.closest !== "function") return;
    var button = target.closest(".clickpost-edd__submit-button");
    var form = button && button.closest(".clickpost-edd__form");
    if (form) startEddCheck(form);
  }

  function onEddKeyUp(event) {
    if (event.key !== "Enter" && event.keyCode !== 13) return;
    var input = event.target;
    if (!input || typeof input.closest !== "function") return;
    if (!input.matches('.clickpost-edd__form input[name="drop_pincode"]')) return;
    startEddCheck(input.closest(".clickpost-edd__form"));
  }

  function init() {
    document.addEventListener("click", onMerchClick, true);
    document.addEventListener("click", onTopCategoryClick, false);
    document.addEventListener("click", onEddClick, true);
    document.addEventListener("keyup", onEddKeyUp, true);
    observeViews();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();
